import React, { useEffect, useState, useContext } from "react";
import * as yup from "yup";
import { Container, Form, FormCheckbox, FormDropdown, FormGroup, FormInput, FormSelect } from "@/components/bp/bp-ui";
import { LabClient, CaseClient, AlleleFormat, QueriesClient } from "@app/api2/api";
import SemanticField from "@app/components/SemanticField";
import { BPForm } from "@common/BPForm";
import { PageHeader } from "@common/PageHeader";
import { getEnabledAlleleFormats } from "@common/AlleleFormatNames";
import { useLabs } from "../Hooks/UseLabs";
import { SelectableItemTyped, useSelectableList } from "../Hooks/UseSelectableList";
import { useTranslation2 } from "../Contexts/TranslationContext2";
import { BPContainer } from "@/components/bp/bpcontainer";

interface CaseCreateFormData {
    name: string;
    lab: number;
    hasQualityControlProfiles: boolean;
    agency?: number;
    alleleFormat?: AlleleFormat;
}

export const CaseCreate: React.FC = () => {
    const { translations: t } = useTranslation2();

    const labClient = new LabClient();
    const queriesClient = new QueriesClient();

    // const [initialValues, setInitialValues] = useState<CaseCreateFormData>({
    //     name: "",
    //     lab: 0,
    //     hasQualityControlProfiles: false,
    //     agency: 0,
    //     alleleFormat: undefined,
    // });
    const [loading, setLoading] = useState(true);
    // const [labList, setLabList] = useState<IDropdownItem<number>[]>([]);
    // const [agencyList, setAgencyList] = useState<IDropdownItem<number>[]>([]);
    // const [alleleFormats, setAlleleFormats] = useState<{ format: AlleleFormat; label: string }[]>([]);
    const [agenciesEnabled, setAgenciesEnabled] = useState(true);
    const [serverErrors, setServerErrors] = useState<string[]>([]);
    const [showServerErrors, setShowServerErrors] = useState(false);

    const labs = useLabs({})
    const agencies = useSelectableList<SelectableItemTyped<number>, number>({
        apiCall: async () => {
            return (await labClient.getLabAgencies(labs.selectedId, false, "", "")).agencies.map((l) => ({ id: l.id, value: l.id, text: l.name }));
        },
        deps: [labs.selectedId],
    })

    const alleleFormats = useSelectableList<SelectableItemTyped<AlleleFormat>, AlleleFormat>({
        apiCall: async () => {
            return (await getEnabledAlleleFormats(labs.selectedId)).map((x) => ({
                id: x.format,
                value: x.format,
                text: x.label,
            }));
        },
        deps: [labs.selectedId],
        defaultId: `${AlleleFormat.CE_RU}` as any,
    })
    const [name, setName] = useState();
    const [hasQualityCheckProfiles, setHasQualityCheckProfiles] = useState(false);


    const validationSchema: yup.SchemaOf<CaseCreateFormData> = yup.object().shape({
        name: yup.string().required().label(window.translation.case.name),
        lab: yup.number().required().label(window.translation.lab.name),
        hasQualityControlProfiles: yup.boolean().label(window.translation.case.hasQualityCheckProfiles),
        agency: yup.number(),
        alleleFormat: yup.mixed().required(),
    });

    useEffect(() => {
        const fetchData = async () => {
            const settings = await queriesClient.getSettings();
            setAgenciesEnabled(settings.agenciesEnabled);
            setLoading(false);
        };
        fetchData();
    }, []);



    const save = async (values: CaseCreateFormData) => {
        const caseClient = new CaseClient();
        const response = await caseClient.create(values.lab, {
            name: values.name,
            labId: values.lab,
            hasQualityCheckProfiles: values.hasQualityControlProfiles,
            agencyId: values.agency,
            alleleFormat: values.alleleFormat,
        });
        if (response.success) {
            window.location.assign(response.url);
        } else {
            setLoading(false);
            setShowServerErrors(true);
            setServerErrors(response.errors);
        }
    };


    return (
        <BPContainer>
            <PageHeader header={window.translation.caseView.create} />
            <BPForm
                enableReinitialize={true}
                initialValues={{ name: name, lab: labs?.selectedId, hasQualityControlProfiles: false, agency: agencies?.selectedId, alleleFormat: alleleFormats?.selectedId }}
                vaildationSchema={validationSchema}
                onSubmit={save}>
                {(props) => (
                    <>
                        <FormGroup>
                            {labs.items.length > 1 && <SemanticField
                                label={window.translation.common.lab}
                                name={'lab'}
                                component={FormDropdown}
                                options={labs.items}
                                selection
                                required
                                value={labs.selectedId}
                                onChange={async (e, dataLabId) => {
                                    labs.setSelectedId(dataLabId);
                                    // await previewSample(formik.values.file, dataLabId, formik.values.kitId, formik.values.format);
                                }} />}
                            {agenciesEnabled && agencies?.items?.length > 0 && (
                                <SemanticField
                                    name={props.getFieldName("agency")}
                                    component={FormSelect}
                                    clearable={true}
                                    width={6}
                                    label={t.common.agency}
                                    options={agencies?.items}
                                    value={agencies?.selectedId}
                                    onChange={async (e, dataAgencyId) => {
                                        agencies.setSelectedId(dataAgencyId);
                                    }}
                                />
                            )}
                        </FormGroup>
                        <SemanticField
                            name={props.getFieldName("name")}
                            component={FormInput}
                            label={t.case.name}
                            width={12}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <SemanticField
                            name={props.getFieldName("hasQualityControlProfiles")}
                            component={FormCheckbox}
                            label={t.case.hasQualityCheckProfiles}
                            checked={hasQualityCheckProfiles}
                            onChange={(e) => setHasQualityCheckProfiles(e.target.checked)}
                        />
                        <SemanticField
                            name="alleleFormat"
                            label={t.common.alleleFormat}
                            component={FormDropdown}
                            selection
                            options={alleleFormats?.items}
                            value={alleleFormats?.selectedId}
                            width={6}
                            error={props.errors.alleleFormat && props.touched.alleleFormat}
                            onChange={async (e, dataFormat) => {
                                alleleFormats.setSelectedId(dataFormat);
                            }}
                        />
                    </>
                )}
            </BPForm>
        </BPContainer>
    );
};